import React from "react";
import { useTranslation } from "react-i18next";
import { useProductAddToCart } from "../../../../cart/hooks/useProductAddToCart";
import { TransformedProduct } from "../../../types/productApiTypes";
import Icon from "../../../../../../common/components/Icon/Icon";
import { useOpenStockNotificationModal } from "../../../../stockNotification/hooks/useOpenStockNotificationModal";

interface CardActionsProps {
  product: TransformedProduct;
  onQuickViewClick: (productId: number) => void;
  showQuickViewButton?: boolean;
}

const CardActions: React.FC<CardActionsProps> = ({
  product,
  onQuickViewClick,
  showQuickViewButton = true,
}) => {
  const { t } = useTranslation();
  const { handleAddToCart, isAddingToCart } = useProductAddToCart();
  const openStockModal = useOpenStockNotificationModal();

  // B2B Approval check
  const isApprovedMember = Boolean(window.commonVariables?.isApprovedMember);
  const loginUrl = window.commonVariables?.loginUrl || "/login";

  // --- B2B CATALOG MODE: Replace cart button with login prompt ---
  if (!isApprovedMember) {
    return (
      <div className="product-card__actions">
        <div className="product-card__button-group">
          {showQuickViewButton && (
            <button
              className="product-card__button"
              title={t("productCard.viewProductTitle")}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onQuickViewClick(product.id);
              }}
            >
              <span>{t("productCard.viewProduct")}</span>
              <Icon iconName="eye-filled" className="icon--filled" />
            </button>
          )}

          <a
            href={loginUrl}
            className="product-card__button product-card__button--login"
            title={t("productCard.loginToBuyTitle", "Giriş Yapın")}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: showQuickViewButton ? "auto" : "100%",
              textDecoration: "none",
            }}
          >
            <span>{t("productCard.loginToBuy", "Giriş Yapın")}</span>
            <Icon iconName="user" className="icon--stroke-thick" />
          </a>
        </div>
      </div>
    );
  }

  // --- External Products ---
  if (product.type === "external") {
    return (
      <div className="product-card__actions">
        <a
          href={product.addToCartUrl}
          className="product-card__button"
          target="_blank"
          rel="noopener noreferrer"
          title={product.addToCartText}
          style={{ width: "100%" }}
        >
          <span>{t("productCard.goToExternalProductLink")}</span>
          <Icon iconName="external-link" className="icon--stroke-thicker" />
        </a>
      </div>
    );
  }

  // --- Variable/Grouped Prompt ---
  if (
    (product.isVariable && !product.hasDefaultVariation) ||
    product.type === "grouped"
  ) {
    return (
      <div className="product-card__actions">
        <button
          className="product-card__button"
          style={{ width: "100%" }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onQuickViewClick(product.id);
          }}
          title={t("productCard.selectOptionsTitle")}
        >
          <span>{t("productCard.selectOptions")}</span>
          <Icon
            iconName="adjustments-horizontal-filled"
            className="icon--filled"
          />
        </button>
      </div>
    );
  }

  const isPurchasable = product.purchasable;
  const isOutOfStock = product.stockStatus === "outofstock";
  const isOnBackorder = product.onBackorder;

  const isAddToCartDisabled = isAddingToCart || !isPurchasable;

  const handleQuickAdd = (e: React.MouseEvent<HTMLButtonElement>) => {
    let idToAdd = product.id;
    let variationData: { attribute: string; value: string }[] | undefined;

    if (
      product.isVariable &&
      product.hasDefaultVariation &&
      product.defaultVariationData
    ) {
      idToAdd = product.defaultVariationData.id;
      variationData = product.defaultVariationData.attributes.map((attr) => ({
        attribute: attr.taxonomy,
        value: attr.term.slug,
      }));
    }

    handleAddToCart(e, {
      productId: idToAdd,
      variation: variationData,
    });
  };

  const handleNotifyMe = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const targetProductId = product.id;
    const targetVariationId =
      product.isVariable &&
      product.hasDefaultVariation &&
      product.defaultVariationData
        ? product.defaultVariationData.id
        : undefined;

    openStockModal({
      productId: targetProductId,
      variationId: targetVariationId,
      productName: product.name,
    });
  };

  return (
    <div className="product-card__actions">
      <div className="product-card__button-group">
        {showQuickViewButton && (
          <button
            className="product-card__button"
            title={t("productCard.viewProductTitle")}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onQuickViewClick(product.id);
            }}
          >
            <span>{t("productCard.viewProduct")}</span>
            <Icon iconName="eye-filled" className="icon--filled" />
          </button>
        )}

        {isOutOfStock && !isOnBackorder ? (
          <button
            className="product-card__button product-card__button--notify"
            title={t("productCard.notifyMeTitle", "Subscribe to alerts")}
            onClick={handleNotifyMe}
          >
            <span>{t("productCard.notifyMe", "Notify Me")}</span>
            <Icon iconName="bell" className="icon--stroke-thickest" />
          </button>
        ) : (
          <button
            className="product-card__button product-card__button--add-to-cart"
            title={t("productCard.addToCartTitle")}
            onClick={handleQuickAdd}
            disabled={isAddToCartDisabled}
          >
            {isAddingToCart ? (
              <span>{t("productCard.addingToCart")}</span>
            ) : !isPurchasable ? (
              <span>{t("productCard.unavailable", "Unavailable")}</span>
            ) : isOnBackorder ? (
              <>
                <span>{t("productCard.backorder", "Backorder")}</span>
                <Icon
                  iconName="shopping-cart-filled"
                  className="icon--filled"
                />
              </>
            ) : (
              <>
                <span>{t("productCard.addToCart")}</span>
                <Icon
                  iconName="shopping-cart-filled"
                  className="icon--filled"
                />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default CardActions;
